import { useSelector } from "react-redux";
import { selectSettings } from "../store/slices/settingsSlice";

/**
 * Tenant-configurable participant terminology.
 *
 * Reads `settings.participant_label_singular` / `_plural` from the
 * Redux settings slice. Falls back to "Athlete" / "Athletes" when
 * settings haven't loaded yet OR the columns aren't populated —
 * matches GoY's experience and the old hardcoded strings.
 *
 * Use cases:
 *   - Yoga studios       → 'Student' / 'Students'
 *   - Martial arts       → 'Practitioner' / 'Practitioners'
 *   - Adult fitness      → 'Member' / 'Members'
 *   - Personal training  → 'Client' / 'Clients'
 *
 * Returns objects with both casings so callers can drop them into
 * sentence positions without re-casing themselves:
 *   { singular: 'Student', plural: 'Students',
 *     singularLower: 'student', pluralLower: 'students' }
 */
export interface ParticipantLabel {
  /** Title-case singular, e.g. 'Athlete'. */
  singular: string;
  /** Title-case plural, e.g. 'Athletes'. */
  plural: string;
  /** Lowercased singular for sentence-middle use, e.g. 'athlete'. */
  singularLower: string;
  /** Lowercased plural for sentence-middle use, e.g. 'athletes'. */
  pluralLower: string;
}

const DEFAULT_SINGULAR = "Athlete";
const DEFAULT_PLURAL = "Athletes";

export function useParticipantLabel(): ParticipantLabel {
  const settings: any = useSelector(selectSettings);
  const singular =
    (settings?.participant_label_singular || "").trim() || DEFAULT_SINGULAR;
  const plural =
    (settings?.participant_label_plural || "").trim() || DEFAULT_PLURAL;
  return {
    singular,
    plural,
    singularLower: singular.toLowerCase(),
    pluralLower: plural.toLowerCase(),
  };
}
